import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Question, ModuleId, FamilyMasteryState, SessionMode } from '../../types';
import { Button } from '../ui/Button';
import { QuestionRendererFactory } from './renderers/QuestionRendererFactory';
import { FeedbackDisplay } from './feedback/FeedbackDisplay';
import { PracticeHeaderMinimal } from './PracticeHeaderMinimal';
import { QuestionCardClean } from './QuestionCardClean';
import { CheatSheetDrawer } from './CheatSheetDrawer';
import { ConfettiCelebration } from '../ConfettiCelebration';
import { getMotivationalMessage, getStreakMessage } from '../../utils/motivationalMessages';
import { useAppStore } from '../../hooks/useAppStore';
import { SESSION_CONFIG, XP_REWARDS } from '../../config/constants';
import { getAdaptiveSessionV11 } from '../../utils/adaptiveEngine';
import { getQuestionTypeConfig } from '../../config/questionTypeRegistry';
import { SessionSummary } from './SessionSummary';
import { Loader2, Sparkles, ArrowRight, AlertCircle } from 'lucide-react';
import { validateSessionAvailability } from '../../utils/sessionValidation';
import { debug, warn, error as logError } from '../../utils/logger';
import { Card } from '../ui/Card';
interface PracticeSessionProps {
  moduleId: ModuleId | null;
  questions: Question[];
  onComplete: (summary?: {
    answeredCount: number;
  }) => void;
  onExit: () => void;
  onAnswer: (questionId: string, isCorrect: boolean, userChoice: number, payload?: string, errorType?: string, delta?: number) => void;
  cheatSheetViewed: boolean;
  onCheatSheetView: () => void;
  isCrossModule?: boolean;
  mode?: SessionMode; // NEW: Session mode (practice or assessment)
}
type Phase = 'question' | 'feedback' | 'summary';
// Calculate XP based on tier and correctness
function calculateXP(isCorrect: boolean, tier?: number): number {
  if (!isCorrect) return XP_REWARDS.EFFORT;
  if (!tier) return XP_REWARDS.CORRECT_EASY;
  if (tier >= 13) return XP_REWARDS.CORRECT_HARD;
  if (tier >= 7) return XP_REWARDS.CORRECT_MEDIUM;
  return XP_REWARDS.CORRECT_EASY;
}
export function PracticeSession({
  moduleId,
  questions,
  onComplete,
  onExit,
  onAnswer,
  cheatSheetViewed,
  onCheatSheetView,
  isCrossModule = false,
  mode = 'practice' // NEW: Default to practice mode
}: PracticeSessionProps) {
  const {
    state,
    selectedGroup,
    groupVersion,
    isInitialized
  } = useAppStore();
  // Session state
  const [sessionQuestions, setSessionQuestions] = useState<Question[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [phase, setPhase] = useState<Phase>('question');
  const [sessionInitialized, setSessionInitialized] = useState(false);
  // NEW: Add initialization error state
  const [initError, setInitError] = useState<string | null>(null);
  // Track group version to detect changes
  const lastGroupVersionRef = useRef(groupVersion);
  // Answer state
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [actualIsCorrect, setActualIsCorrect] = useState<boolean | null>(null);
  const [userTypedAnswer, setUserTypedAnswer] = useState<string>('');
  const [userAnswerIndex, setUserAnswerIndex] = useState<number | null>(null);
  const [interactiveReady, setInteractiveReady] = useState(false);
  const [shouldValidateInteractive, setShouldValidateInteractive] = useState(false);
  // UI state
  const [isCheatSheetOpen, setIsCheatSheetOpen] = useState(false);
  // Stats (disabled in assessment mode)
  const [currentStreak, setCurrentStreak] = useState(0);
  const [motivationalText, setMotivationalText] = useState('');
  const [xpGained, setXpGained] = useState(0);
  const [totalXpGained, setTotalXpGained] = useState(0);
  const [showStreakCelebration, setShowStreakCelebration] = useState(false);
  const [streakMessage, setStreakMessage] = useState('');
  // Session tracking for summary
  const [sessionAnswers, setSessionAnswers] = useState<{
    questionId: string;
    isCorrect: boolean;
  }[]>([]);
  const [preFamilyMastery, setPreFamilyMastery] = useState<Record<string, FamilyMasteryState>>({});
  // NEW: Assessment mode flags
  const isAssessmentMode = mode === 'assessment';
  const showFeedback = !isAssessmentMode; // Only show feedback in practice mode
  const showStreak = !isAssessmentMode; // Only show streak in practice mode
  const showCheatSheet = !isAssessmentMode && !isCrossModule; // Only in practice mode
  // NEW: Styled exit confirmation modal state (replaces window.confirm)
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  // Helper to check if current question is interactive
  const isInteractive = (question?: Question) => {
    if (!question) return false;
    const config = getQuestionTypeConfig(question.questionType || 'multiple_choice');
    return config.isInteractive;
  };
  // Escape key to exit
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isCheatSheetOpen) {
        // NEW: In assessment mode, show styled confirmation modal
        if (isAssessmentMode) {
          setShowExitConfirm(true);
        } else {
          onExit();
        }
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onExit, isCheatSheetOpen, isAssessmentMode]);
  // Initialize session questions (Adaptive) - regenerates on group change
  useEffect(() => {
    // CRITICAL: Wait for useAppStore to finish loading from localStorage
    if (!isInitialized) return;
    // Detect group change mid-session
    if (lastGroupVersionRef.current !== groupVersion) {
      lastGroupVersionRef.current = groupVersion;
      setSessionQuestions([]);
      setSessionInitialized(false);
      setCurrentQuestionIndex(0);
      setPhase('question');
      setSelectedOption(null);
      setUserAnswerIndex(null);
      setUserTypedAnswer('');
      setActualIsCorrect(null);
      setInteractiveReady(false);
      setShouldValidateInteractive(false);
      setCurrentStreak(0);
      setSessionAnswers([]);
      setTotalXpGained(0);
      debug('Session fully reset due to group change');
    }
    if (!sessionInitialized) {
      // NEW: Reset error state
      setInitError(null);
      // Capture pre-session mastery state (SAFE: deep clone)
      if (state.familyMastery) {
        try {
          setPreFamilyMastery(JSON.parse(JSON.stringify(state.familyMastery)));
        } catch (err) {
          logError('Failed to clone familyMastery:', err);
          setPreFamilyMastery({});
        }
      }
      if (isCrossModule) {
        setSessionQuestions(questions);
        setSessionInitialized(true);
      } else if (moduleId) {
        // CRITICAL: Wrap ALL preflight logic in try/catch to prevent crashes
        try {
          // v11.2: FamilyId-first adaptive engine with enhanced pedagogical rules
          const familyMastery = state.familyMastery ?? {};
          // SAFETY: Ensure questionStates is never undefined
          const questionStates = state.questionStates ?? {};
          // Get recent families (last 5) - SAFE ACCESS
          const recentFamilies = Object.values(familyMastery).filter(fm => fm && (fm.lastSeen ?? 0) > 0) // NULL-SAFE: check fm exists and lastSeen
          .sort((a, b) => (b.lastSeen ?? 0) - (a.lastSeen ?? 0)) // NULL-SAFE: sort
          .slice(0, 5).map(fm => fm.familyId).filter(id => id && id.length > 0); // Remove any invalid IDs
          // v11.2: Get recent questions (last 10 from question states) - FULLY SAFE
          const recentQuestions = Object.entries(questionStates).filter(([_, qState]) => qState && (qState.lastAttemptDate ?? 0) > 0) // NULL-SAFE: check qState exists
          .sort((a, b) => (b[1]?.lastAttemptDate ?? 0) - (a[1]?.lastAttemptDate ?? 0)) // NULL-SAFE: sort with optional chaining
          .slice(0, 10).map(([qId, _]) => {
            // FIXED: Extract base question ID (remove group suffix _g{N})
            // Question state keys are in format: "Q-XXX_g6" or just "Q-XXX"
            return qId.replace(/_g\d+$/, ''); // Remove _g{digit(s)} from end
          }).filter(qId => qId && qId.length > 0); // Filter out any empty strings
          debug(`v11.2: Generating session for ${moduleId} | Group ${selectedGroup} | Families tracked: ${Object.keys(familyMastery).length}`);
          debug(`   Recent families (cooldown): ${recentFamilies.length > 0 ? recentFamilies.join(', ') : 'none'}`);
          debug(`   Recent questions (exclude): ${recentQuestions.length > 0 ? recentQuestions.slice(0, 5).join(', ') + (recentQuestions.length > 5 ? '...' : '') : 'none'}`);
          const adaptiveSession = getAdaptiveSessionV11(moduleId, familyMastery, selectedGroup, questionStates,
          // SAFE: guaranteed to be an object
          SESSION_CONFIG.QUESTIONS_PER_SESSION, recentFamilies, recentQuestions);
          // Check if session is empty and set error
          if (adaptiveSession.length === 0) {
            warn('Empty session generated - all content mastered or unavailable');
            setInitError('empty_session');
          }
          setSessionQuestions(adaptiveSession);
          setSessionInitialized(true);
          debug(`Session: ${adaptiveSession.length} questions for ${moduleId} (group ${selectedGroup}, engine: v11.2, mode: ${mode})`);
        } catch (error) {
          logError('Failed to generate session:', error);
          logError('Error details:', {
            moduleId,
            selectedGroup,
            familyMasteryKeys: Object.keys(state.familyMastery ?? {}).length,
            questionStatesKeys: Object.keys(state.questionStates ?? {}).length,
            hasQuestionStates: !!state.questionStates,
            hasFamilyMastery: !!state.familyMastery
          });
          setInitError('generation_failed');
          setSessionQuestions([]);
          setSessionInitialized(true);
        }
      }
    }
  }, [moduleId, isCrossModule, questions, sessionInitialized, selectedGroup, groupVersion, isInitialized, state.familyMastery, state.questionStates, mode]);
  const currentQuestion = sessionQuestions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === sessionQuestions.length - 1;
  // Reset answer state when question changes
  useEffect(() => {
    if (!currentQuestion) return;
    setSelectedOption(null);
    setUserAnswerIndex(null);
    setUserTypedAnswer('');
    setInteractiveReady(false);
    setShouldValidateInteractive(false);
  }, [currentQuestion]);
  // Enter key handler - MOVED TO TOP to fix hook order violation
  useEffect(() => {
    // Guard: only run if we have a current question
    if (!currentQuestion) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (phase !== 'question' || isCheatSheetOpen) return;
      if (e.key === 'Enter') {
        const interactive = isInteractive(currentQuestion);
        if (interactive && interactiveReady) {
          submitAnswer();
        } else if (!interactive && userAnswerIndex !== null) {
          submitAnswer();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [phase, isCheatSheetOpen, userAnswerIndex, interactiveReady, currentQuestion]);
  // NEW: Loading state - show while waiting for initialization
  if (!isInitialized || !sessionInitialized) {
    return <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAFAF8]" data-id="element-4729">
        <div className="text-center" data-id="element-4730">
          <motion.div animate={{
          scale: [1, 1.1, 1],
          rotate: [0, 5, -5, 0]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="w-24 h-24 bg-indigo-100 rounded-full mx-auto mb-6 flex items-center justify-center text-indigo-600 shadow-lg shadow-indigo-200/50" data-id="element-4731">
            <Loader2 className="w-12 h-12 animate-spin" data-id="element-4732" />
          </motion.div>
          <h3 className="text-2xl font-black text-slate-800 mb-3" data-id="element-4733">
            Even geduld...
          </h3>
          <p className="text-slate-500 font-medium text-lg" data-id="element-4734">
            {isAssessmentMode ? 'We maken je toets klaar! ✨' : 'We zoeken de beste vragen voor jou! ✨'}
          </p>
        </div>
      </div>;
  }
  // NEW: Error state - show if session generation failed
  if (initError === 'generation_failed') {
    return <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center p-6" data-id="element-4735">
        <div className="max-w-lg w-full p-12 text-center bg-white rounded-[2.5rem] shadow-2xl shadow-rose-100 border-2 border-rose-100" data-id="element-4736">
          <div className="text-6xl mb-6" data-id="element-4737">😕</div>
          <h2 className="text-3xl font-black text-slate-900 mb-4 tracking-tight" data-id="element-4738">
            Er ging iets mis
          </h2>
          <p className="text-slate-600 mb-8 text-lg leading-relaxed font-medium" data-id="element-4739">
            {isAssessmentMode ? 'De toets kon niet worden geladen.' : 'De oefensessie kon niet worden geladen.'}
          </p>
          <Button onClick={onExit} variant="primary" size="xxl" fullWidth className="shadow-2xl shadow-indigo-200" data-id="element-4740">
            Terug naar overzicht
          </Button>
        </div>
      </div>;
  }
  // Empty state - all mastered (ENHANCED with validation)
  if (sessionInitialized && sessionQuestions.length === 0) {
    // NEW: Check WHY the session is empty using validation
    const validationReport = moduleId ? validateSessionAvailability(moduleId, selectedGroup) : null;
    const hasContent = validationReport?.isValid ?? false;
    const hasAnyProgress = state.familyMastery && Object.keys(state.familyMastery).some(fId => {
      const fm = state.familyMastery![fId];
      return fm.attempts > 0;
    });
    // Case 1: No content available for this group (content gap)
    if (!hasContent) {
      return <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center p-6" data-id="element-4741">
          <div className="max-w-lg w-full p-12 text-center bg-white rounded-[2.5rem] shadow-2xl shadow-amber-100 border-2 border-amber-100" data-id="element-4742">
            <motion.div animate={{
            rotate: [0, -10, 10, -10, 0]
          }} transition={{
            duration: 0.5,
            repeat: Infinity,
            repeatDelay: 2
          }} className="text-8xl mb-6 drop-shadow-lg" data-id="element-4743">
              📚
            </motion.div>
            <h2 className="text-4xl font-black text-slate-900 mb-4 tracking-tight" data-id="element-4744">
              Nog geen vragen beschikbaar
            </h2>
            <p className="text-slate-600 mb-6 text-xl leading-relaxed font-medium" data-id="element-4745">
              Voor dit vak zijn er nog geen vragen beschikbaar voor groep{' '}
              {selectedGroup}.
            </p>
            <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-5 mb-8" data-id="element-4746">
              <div className="flex items-start gap-3 text-left" data-id="element-4747">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-4748" />
                <div className="text-sm text-amber-800 font-medium leading-relaxed" data-id="element-4749">
                  <p className="mb-2" data-id="element-4750">
                    <strong data-id="element-4751">Wat kun je doen?</strong>
                  </p>
                  <ul className="space-y-1 list-disc list-inside" data-id="element-4752">
                    <li data-id="element-4753">Probeer een ander vak</li>
                    <li data-id="element-4754">Kom later terug voor nieuwe vragen</li>
                    {selectedGroup > 6 && <li data-id="element-4755">Wissel naar een andere groep in de instellingen</li>}
                  </ul>
                </div>
              </div>
            </div>
            <Button onClick={onExit} variant="primary" size="xxl" fullWidth className="shadow-2xl shadow-indigo-200" data-id="element-4756">
              Terug naar overzicht
            </Button>
          </div>
        </div>;
    }
    // Case 2: Content exists but all mastered (actual completion)
    if (hasAnyProgress) {
      return <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center p-6" data-id="element-4757">
          <div className="max-w-lg w-full p-12 text-center bg-white rounded-[2.5rem] shadow-2xl shadow-indigo-100 border-2 border-indigo-100" data-id="element-4758">
            <motion.div animate={{
            scale: [1, 1.1, 1],
            rotate: [0, 5, -5, 0]
          }} transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="text-8xl mb-6 drop-shadow-lg" data-id="element-4759">
              🌟
            </motion.div>
            <h2 className="text-4xl font-black text-slate-900 mb-4 tracking-tight" data-id="element-4760">
              Geweldig gedaan!
            </h2>
            <p className="text-slate-600 mb-8 text-xl leading-relaxed font-medium" data-id="element-4761">
              Je hebt alle vragen voor dit vak in groep {selectedGroup} super
              goed geoefend! 🎉
            </p>
            <p className="text-amber-600 font-bold text-lg mb-10" data-id="element-4762">
              Probeer een ander vak of kom later terug voor meer uitdaging!
            </p>
            <Button onClick={onExit} variant="primary" size="xxl" fullWidth className="shadow-2xl shadow-indigo-200" data-id="element-4763">
              <Sparkles className="w-6 h-6 mr-2 fill-current" data-id="element-4764" />
              Terug naar overzicht
            </Button>
          </div>
        </div>;
    }
    // Case 3: Content exists but no progress yet (shouldn't happen, but handle it)
    return <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center p-6" data-id="element-4765">
        <div className="max-w-lg w-full p-12 text-center bg-white rounded-[2.5rem] shadow-2xl shadow-rose-100 border-2 border-rose-100" data-id="element-4766">
          <div className="text-6xl mb-6" data-id="element-4767">🤔</div>
          <h2 className="text-3xl font-black text-slate-900 mb-4 tracking-tight" data-id="element-4768">
            Er ging iets mis
          </h2>
          <p className="text-slate-600 mb-8 text-lg leading-relaxed font-medium" data-id="element-4769">
            {isAssessmentMode ? 'De toets kon niet worden gemaakt.' : 'We konden geen oefensessie maken.'}{' '}
            Probeer het nog eens!
          </p>
          <Button onClick={onExit} variant="primary" size="xxl" fullWidth className="shadow-2xl shadow-indigo-200" data-id="element-4770">
            Terug naar overzicht
          </Button>
        </div>
      </div>;
  }
  // NEW: Safety check - if currentQuestion is undefined but we have questions, something is wrong
  if (!currentQuestion && sessionQuestions.length > 0) {
    logError('Current question is undefined but session has questions. Index:', currentQuestionIndex);
    return <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center p-6" data-id="element-4771">
        <div className="max-w-lg w-full p-12 text-center bg-white rounded-[2.5rem] shadow-2xl shadow-rose-100 border-2 border-rose-100" data-id="element-4772">
          <div className="text-6xl mb-6" data-id="element-4773">🤔</div>
          <h2 className="text-3xl font-black text-slate-900 mb-4 tracking-tight" data-id="element-4774">
            Er ging iets mis
          </h2>
          <p className="text-slate-600 mb-8 text-lg leading-relaxed font-medium" data-id="element-4775">
            We konden de vraag niet laden. Probeer het nog eens!
          </p>
          <Button onClick={onExit} variant="primary" size="xxl" fullWidth className="shadow-2xl shadow-indigo-200" data-id="element-4776">
            Terug naar overzicht
          </Button>
        </div>
      </div>;
  }
  const handleCheatSheetClose = () => {
    setIsCheatSheetOpen(false);
    if (!cheatSheetViewed) {
      onCheatSheetView();
    }
  };
  const processAnswer = (isCorrect: boolean, distractorIndex: number, errorType?: string, delta?: number, payload?: string) => {
    // NEW: Only calculate XP and streak in practice mode
    if (!isAssessmentMode) {
      const xp = calculateXP(isCorrect, currentQuestion.tier);
      setXpGained(xp);
      setTotalXpGained(prev => prev + xp);
      if (isCorrect) {
        const newStreak = currentStreak + 1;
        setCurrentStreak(newStreak);
        const message = getStreakMessage(newStreak);
        if (message) {
          setStreakMessage(message);
          setShowStreakCelebration(true);
        }
      } else {
        setCurrentStreak(0);
      }
      const message = getMotivationalMessage({
        isCorrect,
        streak: currentStreak,
        questionNumber: currentQuestionIndex + 1,
        totalQuestions: sessionQuestions.length
      });
      setMotivationalText(message);
    }
    setUserAnswerIndex(distractorIndex);
    onAnswer(currentQuestion.id, isCorrect, distractorIndex, payload, errorType, delta);
    // Track answer for summary
    setSessionAnswers(prev => [...prev, {
      questionId: currentQuestion.id,
      isCorrect
    }]);
    setSelectedOption(null);
    // NEW: In assessment mode, skip feedback and go directly to next question
    if (isAssessmentMode) {
      // Small delay for visual feedback on button click
      setTimeout(() => {
        nextQuestion();
      }, 300);
    } else {
      setPhase('feedback');
    }
  };
  const handleInteractiveComplete = (userAnswer: string) => {
    setUserTypedAnswer(userAnswer);
    const config = getQuestionTypeConfig(currentQuestion.questionType || 'multiple_choice');
    const validation = config.validateAnswer(userAnswer, currentQuestion);
    const isCorrect = validation.isCorrect;
    const distractorIndex = validation.distractorIndex ?? 0;
    if (validation.displayAnswer) {
      setUserTypedAnswer(validation.displayAnswer);
    }
    setActualIsCorrect(isCorrect);
    processAnswer(isCorrect, distractorIndex, validation.errorType, validation.delta, userAnswer);
  };
  const handleRendererAnswer = (userAnswer: string | number) => {
    if (isInteractive(currentQuestion)) {
      handleInteractiveComplete(userAnswer as string);
    } else {
      setUserAnswerIndex(userAnswer as number);
      setSelectedOption(userAnswer as number);
    }
  };
  const submitAnswer = () => {
    if (isInteractive(currentQuestion)) {
      setShouldValidateInteractive(true);
      return;
    }
    if (userAnswerIndex === null) return;
    const isCorrect = userAnswerIndex === currentQuestion.correctAnswer;
    processAnswer(isCorrect, userAnswerIndex);
  };
  const nextQuestion = () => {
    if (isLastQuestion) {
      // In assessment mode, skip the practice summary and go directly to assessment report
      if (isAssessmentMode) {
        onComplete({
          answeredCount: sessionQuestions.length
        });
        return;
      }
      setPhase('summary');
    } else {
      setCurrentQuestionIndex(prev => prev + 1);
      setSelectedOption(null);
      setUserAnswerIndex(null);
      setUserTypedAnswer('');
      setActualIsCorrect(null);
      setInteractiveReady(false);
      setShouldValidateInteractive(false);
      setPhase('question');
    }
  };
  if (phase === 'summary') {
    return <SessionSummary moduleId={moduleId!} sessionQuestions={sessionQuestions} sessionAnswers={sessionAnswers} preFamilyMastery={preFamilyMastery} onExit={() => onComplete({
      answeredCount: sessionQuestions.length
    })} onPracticeAgain={() => {
      // Reset session state to re-initialize with fresh adaptive questions
      setSessionQuestions([]);
      setSessionInitialized(false);
      setCurrentQuestionIndex(0);
      setPhase('question');
      setSelectedOption(null);
      setUserAnswerIndex(null);
      setUserTypedAnswer('');
      setActualIsCorrect(null);
      setInteractiveReady(false);
      setShouldValidateInteractive(false);
      setCurrentStreak(0);
      setSessionAnswers([]);
      setTotalXpGained(0);
    }} xpGained={totalXpGained} data-id="element-4777" />;
  }
  if (!currentQuestion) {
    return <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAFAF8]" data-id="element-4778">
        <div className="text-center" data-id="element-4779">
          <motion.div animate={{
          scale: [1, 1.1, 1],
          rotate: [0, 5, -5, 0]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="w-24 h-24 bg-indigo-100 rounded-full mx-auto mb-6 flex items-center justify-center text-indigo-600 shadow-lg shadow-indigo-200/50" data-id="element-4780">
            <Loader2 className="w-12 h-12 animate-spin" data-id="element-4781" />
          </motion.div>
          <h3 className="text-2xl font-black text-slate-800 mb-3" data-id="element-4782">
            Even geduld...
          </h3>
          <p className="text-slate-500 font-medium text-lg" data-id="element-4783">
            {isAssessmentMode ? 'We maken je toets klaar! ✨' : 'We zoeken de beste vragen voor jou! ✨'}
          </p>
        </div>
      </div>;
  }
  return <div className={`min-h-screen bg-[#FAFAF8] flex flex-col font-sans ${isAssessmentMode ? 'h-screen overflow-hidden' : ''}`} data-id="element-4784">
      {/* Only show confetti in practice mode */}
      {showStreak && <ConfettiCelebration trigger={showStreakCelebration} message={streakMessage} data-id="element-4785" />}

      {/* Only show cheat sheet in practice mode */}
      {showCheatSheet && <CheatSheetDrawer isOpen={isCheatSheetOpen} onClose={handleCheatSheetClose} moduleId={moduleId!} subSkill={currentQuestion.subSkill} familyId={currentQuestion.familyId} data-id="element-4786" />}

      <PracticeHeaderMinimal currentQuestion={currentQuestionIndex + 1} totalQuestions={sessionQuestions.length} streak={showStreak ? currentStreak : undefined} onExit={() => {
      // NEW: In assessment mode, show styled confirmation modal
      if (isAssessmentMode) {
        setShowExitConfirm(true);
      } else {
        onExit();
      }
    }} onToggleCheatSheet={showCheatSheet ? () => setIsCheatSheetOpen(true) : undefined} isCheatSheetOpen={isCheatSheetOpen} data-id="element-4787" />

      {/* MOBILE OPTIMIZED CONTAINER - Reduced padding, better spacing */}
      <div className={`flex-1 max-w-4xl mx-auto w-full px-3 sm:px-4 md:px-6 py-3 sm:py-4 md:py-6 flex flex-col ${isAssessmentMode ? 'min-h-0 overflow-y-auto pb-8' : 'min-h-[calc(100vh-64px)] justify-center'}`} data-id="element-4788">
        <AnimatePresence mode="wait" data-id="element-4789">
          {phase === 'question' && <motion.div key={`question-${currentQuestionIndex}`} initial={{
          opacity: 0,
          y: 20,
          scale: 0.98
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: -20,
          scale: 0.98
        }} transition={{
          duration: 0.4,
          ease: 'easeOut'
        }} className="w-full" data-id="element-4790">
              <div data-id="element-4791">
                <QuestionCardClean question={currentQuestion} data-id="element-4792">
                  <QuestionRendererFactory question={currentQuestion} onAnswer={handleRendererAnswer} onReadyChange={setInteractiveReady} shouldValidate={shouldValidateInteractive} data-id="element-4793" />
                </QuestionCardClean>
              </div>

              {/* MOBILE OPTIMIZED BUTTON - Reduced spacing */}
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="mt-4 sm:mt-6 md:mt-8 pb-4 flex justify-center" data-id="element-4794">
                <Button size="xl" disabled={isInteractive(currentQuestion) ? !interactiveReady : userAnswerIndex === null} onClick={submitAnswer} className="w-full sm:w-auto sm:min-w-[280px] md:min-w-[360px] shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1 rounded-2xl text-fluid-lg" data-id="element-4795">
                  <span className="mr-2 font-black" data-id="element-4796">Controleren</span>
                  <ArrowRight className="w-5 h-5" strokeWidth={3} data-id="element-4797" />
                </Button>
              </motion.div>
            </motion.div>}

          {phase === 'feedback' && showFeedback && <motion.div key="feedback" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.3,
          type: 'spring',
          bounce: 0.2
        }} className="w-full" data-id="element-4798">
              <FeedbackDisplay question={currentQuestion} wasCorrect={isInteractive(currentQuestion) ? actualIsCorrect === true : userAnswerIndex !== null && userAnswerIndex === currentQuestion.correctAnswer} userAnswer={isInteractive(currentQuestion) ? userTypedAnswer : userAnswerIndex !== null ? currentQuestion.options[userAnswerIndex] : ''} userAnswerIndex={userAnswerIndex ?? undefined} motivationalText={motivationalText} xpGained={xpGained} onNext={nextQuestion} isLastQuestion={isLastQuestion} mode="full" data-id="element-4799" />
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* NEW: Styled Assessment Exit Confirmation Modal */}
      <AnimatePresence data-id="element-4800">
        {showExitConfirm && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50" onClick={() => setShowExitConfirm(false)} data-id="element-4801" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.9,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.9,
          y: 20
        }} transition={{
          type: 'spring',
          bounce: 0.2,
          duration: 0.4
        }} className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-4802">
              <Card className="max-w-md w-full p-8 shadow-2xl border-2 border-amber-100 bg-white rounded-3xl" data-id="element-4803">
                <div className="text-center" data-id="element-4804">
                  <div className="text-5xl mb-4" data-id="element-4805">🤔</div>
                  <h2 className="text-2xl font-black text-slate-900 mb-3 tracking-tight" data-id="element-4806">
                    Toets stoppen?
                  </h2>
                  <p className="text-slate-600 font-medium mb-6 leading-relaxed" data-id="element-4807">
                    Weet je zeker dat je wilt stoppen? Je voortgang wordt
                    opgeslagen als onvolledig.
                  </p>
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-left" data-id="element-4808">
                    <div className="flex items-start gap-3" data-id="element-4809">
                      <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-4810" />
                      <p className="text-sm text-amber-800 font-medium" data-id="element-4811">
                        Je hebt {currentQuestionIndex + 1} van de{' '}
                        {sessionQuestions.length} vragen beantwoord. Als je
                        stopt, kun je later opnieuw beginnen.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-3" data-id="element-4812">
                    <Button onClick={() => setShowExitConfirm(false)} variant="primary" size="lg" fullWidth data-id="element-4813">
                      Doorgaan met toets
                    </Button>
                    <Button onClick={() => {
                  setShowExitConfirm(false);
                  onExit();
                }} variant="secondary" size="lg" fullWidth className="text-rose-600 border-rose-200 hover:bg-rose-50" data-id="element-4814">
                      Stoppen
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          </>}
      </AnimatePresence>
    </div>;
}